import { sleep } from '@utils/time';
import type { EventCredentialsResponse } from '@repository/types';

export const DEFAULT_MARGIN_IN_PX = 50;
// The height is set to 175 because then logged out banner on mobile can
// fit properly and additional hight manipulation logic is not required
export const DEFAULT_BANNER_HEIGHT_IN_PX = 175;

export interface BannerHeightData {
    totalHeight: string;
    marginTop: string;
    position: string;
}

export type MessageSender = <T>(eventName: string, data: T) => void;

export const useBannerPostMessage = (messageSender: MessageSender) => {
    const getDesktopBannerHeightData = async (
        isOpen: boolean
    ): Promise<BannerHeightData> => {
        await sleep(10);
        const popper = document.querySelector('.popper');
        const header = document.querySelector('header');

        const popperHeight = popper?.clientHeight || 275;
        const headerHeight = header?.clientHeight || 84;

        const totalHeight = isOpen
            ? popperHeight + headerHeight
            : DEFAULT_BANNER_HEIGHT_IN_PX;

        const marginTop = isOpen
            ? DEFAULT_BANNER_HEIGHT_IN_PX + DEFAULT_MARGIN_IN_PX - totalHeight
            : DEFAULT_MARGIN_IN_PX;

        return {
            totalHeight: totalHeight + 'px',
            marginTop: marginTop + 'px',
            position: 'relative',
        };
    };

    const getMobileBannerHeightData = (isOpen: boolean): BannerHeightData => {
        return {
            totalHeight: isOpen ? '100vh' : DEFAULT_BANNER_HEIGHT_IN_PX + 'px',
            marginTop: '200px',
            position: 'fixed',
        };
    };

    const handlePostBannerStyles = async (
        isOpen: boolean,
        isMobile: boolean
    ) => {
        const { marginTop, position, totalHeight } = isMobile
            ? getMobileBannerHeightData(isOpen)
            : await getDesktopBannerHeightData(isOpen);
        postBannerHeightData(isOpen, totalHeight, marginTop, position);
    };

    const postBannerHeightData = (
        isOpen: boolean,
        height: string,
        marginTop: string,
        position: string
    ) => {
        messageSender('setBannerHeight', {
            height,
            marginTop,
            isOpen,
            position,
        });
    };

    const postIsUserLoggedIn = (isUserLoggedIn: boolean) => {
        messageSender('setIsUserLoggedIn', { isUserLoggedIn });
    };

    const postUserData = (data: EventCredentialsResponse) => {
        messageSender('setInputsValues', data);
    };

    return {
        postUserData,
        handlePostBannerStyles,
        postIsUserLoggedIn,
    };
};
